import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type {
  ProductListingState,
  ProductListingFilters,
  Product,
  Store,
  ProductListingPagination
} from '../types/ProductListing'
import ProductListingService from '../services/productListingService'
import { getAccountService } from 'src/shared/services'

const initialState: ProductListingState = {
  products: [],
  filteredProducts: [],
  loading: false,
  error: null,
  filters: {
    accountId: '',
    search: '',
    status: 'all',
    stores: [],
    sortBy: 'title',
    sortOrder: 'asc',
  },
  selectedProducts: [],
  stores: [],
  storesLoading: false,
  storesError: null,
  pagination: {
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  },
}

// Async thunk for fetching product listings
export const fetchProductListings = createAsyncThunk(
  'productListing/fetchProductListings',
  async (params: { filters: ProductListingFilters; page?: number; pageSize?: number }) => {
    const service = ProductListingService.getInstance()
    if (params.page && params.pageSize) {
      return service.getProductListingsWithPagination(params.filters, params.page, params.pageSize)
    }
    return service.getProductListingsByFilters(params.filters)
  }
)

// Async thunk for refreshing product listings
export const refreshProductListings = createAsyncThunk(
  'productListing/refreshProductListings',
  async (_, { getState }) => {
    const state = getState() as { productListing: ProductListingState }
    const service = ProductListingService.getInstance()
    return service.getProductListingsWithPagination(
      state.productListing.filters,
      state.productListing.pagination.page,
      state.productListing.pagination.pageSize
    )
  }
)

// Async thunk for fetching accessible stores
export const fetchAccessibleStores = createAsyncThunk(
  'productListing/fetchAccessibleStores',
  async () => {
    // Service registry automatically handles mock vs real implementation
    const accountService = getAccountService()
    const accounts = await accountService.getAccessibleAccounts()

    if (!accounts || accounts.length === 0) {
      throw new Error('No accessible accounts found')
    }

    // Map accounts to Store format
    const stores: Store[] = accounts.map(account => ({
      id: account.id,
      name: account.name,
      marketplace: account.marketplace,
      logo: account.logo,
      url: account.url
    }))

    return stores
  }
)

const productListingSlice = createSlice({
  name: 'productListing',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<ProductListingFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload
    },
    setStatusFilter: (state, action: PayloadAction<ProductListingState['filters']['status']>) => {
      state.filters.status = action.payload
    },
    setStoreFilter: (state, action: PayloadAction<string[]>) => {
      state.filters.stores = action.payload
    },
    setSortField: (state, action: PayloadAction<{ field: ProductListingFilters['sortBy']; direction: ProductListingFilters['sortOrder'] }>) => {
      state.filters.sortBy = action.payload.field
      state.filters.sortOrder = action.payload.direction
    },
    setSelectedProducts: (state, action: PayloadAction<string[]>) => {
      state.selectedProducts = action.payload
    },
    toggleProductSelection: (state, action: PayloadAction<string>) => {
      const productId = action.payload
      if (state.selectedProducts.includes(productId)) {
        state.selectedProducts = state.selectedProducts.filter(id => id !== productId)
      } else {
        state.selectedProducts.push(productId)
      }
    },
    clearSelectedProducts: (state) => {
      state.selectedProducts = []
    },
    setStores: (state, action: PayloadAction<Store[]>) => {
      state.stores = action.payload
    },
    setPagination: (state, action: PayloadAction<Partial<ProductListingPagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload
      state.pagination.page = 1 // Reset to first page when changing page size
    },
    clearError: (state) => {
      state.error = null
    },
    clearStoresError: (state) => {
      state.storesError = null
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchProductListings
      .addCase(fetchProductListings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProductListings.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload?.success) {
          state.products = action.payload.data.productListings
          state.filteredProducts = action.payload.data.productListings
          state.pagination = action.payload.data.pagination
        } else {
          state.error = 'Failed to fetch product listings'
        }
      })
      .addCase(fetchProductListings.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch product listings'
      })
      // refreshProductListings
      .addCase(refreshProductListings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(refreshProductListings.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload?.success) {
          state.products = action.payload.data.productListings
          state.filteredProducts = action.payload.data.productListings
          state.pagination = action.payload.data.pagination
        } else {
          state.error = 'Failed to refresh product listings'
        }
      })
      .addCase(refreshProductListings.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to refresh product listings'
      })
      // fetchAccessibleStores
      .addCase(fetchAccessibleStores.pending, (state) => {
        state.storesLoading = true
        state.storesError = null
      })
      .addCase(fetchAccessibleStores.fulfilled, (state, action) => {
        state.storesLoading = false
        state.stores = action.payload
        // Auto-select all stores by default
        state.filters.stores = action.payload.map(store => store.id)
      })
      .addCase(fetchAccessibleStores.rejected, (state, action) => {
        state.storesLoading = false
        state.storesError = action.error.message || 'Failed to fetch accessible stores'
      })
  },
})

export const {
  setFilters,
  setSearchTerm,
  setStatusFilter,
  setStoreFilter,
  setSortField,
  setSelectedProducts,
  toggleProductSelection,
  clearSelectedProducts,
  setStores,
  setPagination,
  setPage,
  setPageSize,
  clearError,
  clearStoresError,
  resetState,
} = productListingSlice.actions

export default productListingSlice.reducer